import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import InquiryForm from "@/components/InquiryForm";
import SEO from "@/components/SEO";
import { useSiteSection } from "@/lib/useSiteSection";
import { buildBreadcrumb } from "@/lib/schemas";

export default function ConsultationPage() {
  const hero = useSiteSection("consultation_hero", {
    eyebrow: "On-location consulting",
    heading: "Request a Homestead Consultation",
    body: "Tell us a bit about your land and what you're trying to build. Heather will reply within 1–2 business days with available dates and a final quote.",
  });
  return (
    <>
      <SEO
        title="Request a Homestead & Permaculture Consultation"
        description="On-location homestead and permaculture consulting in Chino Valley, Prescott, and across Yavapai County. Walked on-site, prioritized PDF summary within 3-5 days."
        keywords="Arizona permaculture consultant, homestead consultant Yavapai County, Chino Valley property consultation, land design Prescott"
        jsonLd={buildBreadcrumb([
          { name: "Home", url: "/" },
          { name: "1-on-1 & Consulting", url: "/services" },
          { name: "Property Consultation", url: "/consultation" },
        ])}
      />
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-20">
        <header className="fade-up">
          <p className="uppercase tracking-[0.22em] text-xs sm:text-sm text-gg-brown font-semibold">
            {hero.eyebrow}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] leading-tight mt-3">
            {hero.heading}
          </h1>
          <p className="mt-5 text-gg-muted leading-relaxed">
            {hero.body}
          </p>
        </header>

        <div className="mt-10 fade-up d1">
          <InquiryForm
            requestType="consultation"
            testidPrefix="consultation"
            submitLabel="Send Consultation Request"
            fields={[
              {
                name: "property_address",
                label: "Property address",
                required: true,
                placeholder: "Street, City, State, ZIP",
                hint: "Used to calculate travel fee.",
              },
              {
                name: "property_size",
                label: "Property size",
                type: "select",
                required: true,
                options: [
                  { value: "lt-0.25", label: "Under 1/4 acre (suburban lot)" },
                  { value: "0.25-1", label: "1/4 – 1 acre" },
                  { value: "1-5", label: "1 – 5 acres" },
                  { value: "5+", label: "5+ acres (large acreage)" },
                ],
              },
              {
                name: "consultation_hours",
                label: "Visit length",
                type: "select",
                required: true,
                options: [
                  { value: "1", label: "1 hour — $300" },
                  { value: "2", label: "2 hours — $500" },
                  { value: "3", label: "3 hours — $700" },
                ],
                hint: "Travel fee added based on your address.",
              },
              {
                name: "consultation_addons",
                label: "Add-ons (optional)",
                type: "checkbox-list",
                options: [
                  {
                    value: "backyard-layout",
                    label: "Backyard Layout",
                    price: "+$175",
                    note: "Color-coded satellite map, custom plant list, seasonal checklist. Best for suburban lots.",
                  },
                  {
                    value: "homestead-framework",
                    label: "Homestead Framework",
                    price: "+$350",
                    note: "Permaculture map, 12-month workflow, ecosystem strategy. Best for 1–5 acres.",
                  },
                  {
                    value: "large-acreage",
                    label: "Large Acreage Plan (5+ acres)",
                    price: "Custom quote",
                    note: "Water management, orchard / food-forest, multi-year buildout in phases.",
                  },
                ],
              },
            ]}
          />
        </div>
      </main>
      <Footer />
    </>
  );
}
